import { useCallback, useState } from "react";
import { Box, Backdrop, Button, Card, CardContent, Dialog, Typography, Divider, Alert, IconButton } from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import FormikErrorFocus from "formik-error-focus";
import { useDispatch, useSelector } from "react-redux";
import { Close } from "@mui/icons-material";
import * as Yup from "yup";
import PropTypes from "prop-types";

import axios from "../utils/axios";
import { setProfile } from "../reducers/profile";
import TextField from "./TextField";
import GoogleSignInButton from "./GoogleSignInButton";
import { BACKDROP_COLOR } from "../styles/theme";

const cardPadding = { padding: [0, 2, 4] };

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: (props) => (props.mode === "light" ? "white" : undefined),
  },
  cardContent: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  form: {
    width: "100%",
    maxWidth: 400,
  },
  link: {
    color: "black",
    fontFamily: "Raleway",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "14px",
    letterSpacing: "0.6px",
    marginBottom: 10,
  },
}));
export default function SignIn({ open, onClose, activityId = undefined }) {
  const { mode } = useSelector((state) => state.style);
  const classes = useStyles({ mode });
  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);
  const [unverifiedUserId, setUnverifiedUserId] = useState(null);
  const [resendState, setResendState] = useState({ loading: false, message: "" });

  const handleSubmitFormik = useCallback(
    async (values, { setSubmitting }) => {
      try {
        setErrors([]);
        setUnverifiedUserId(null);
        setResendState({ loading: false, message: "" });
        // Step 1: Login and receive token
        const loginResponse = await axios.post("auth/login", values);

        // Assume backend returns { access_token: "..." }
        const token = loginResponse.data.access_token;
        if (!token) {
          throw new Error("No token returned from API");
        }

        // Save token to localStorage with app-specific key to avoid conflicts with sva-admin
        localStorage.setItem("sva_webapp_token", token);
        if (activityId) {
          await axios.get(`activities/join/${activityId}`, { withCredentials: true });
        }

        const response = await axios.get("auth/profile", { withCredentials: true });
        dispatch(setProfile(response.data.data));
        setSubmitting(false);
        onClose();
      } catch (error) {
        // Handle different error response formats
        const errorData = error.response?.data;

        // Unverified email: the API returns the user's id so we can offer a resend.
        if (errorData?.unverified_user_id) {
          setUnverifiedUserId(errorData.unverified_user_id);
        }

        if (errorData?.errors && Object.keys(errorData.errors).length > 0) {
          // Format: { errors: { email: ["message"] } }
          setErrors(Object.values(errorData.errors).flat());
        } else if (errorData?.message) {
          // Format: { message: "error message" } or { status: false, message: "error", errors: [] }
          setErrors([errorData.message]);
        } else if (error.message) {
          setErrors([error.message]);
        } else {
          setErrors(["An unexpected error occurred. Please try again."]);
        }
        setSubmitting(false);
      }
    },
    [dispatch, onClose, activityId]
  );

  const handleResendVerification = useCallback(async () => {
    if (!unverifiedUserId) return;
    setResendState({ loading: true, message: "" });
    try {
      await axios.post(`auth/verify/resend/${unverifiedUserId}`);
      setResendState({ loading: false, message: "Verification email sent — please check your inbox (and spam folder)." });
    } catch (error) {
      const alreadyVerified = error.response?.data?.errors?.verify === "Already verified";
      setResendState({
        loading: false,
        message: alreadyVerified
          ? "This email is already verified — please try signing in again."
          : "Couldn't resend just now. Please try again shortly.",
      });
    }
  }, [unverifiedUserId]);

  const handleNavigateToSignUp = useCallback(() => {
    onClose(true);
  }, [onClose]);

  const handleGoogleSuccess = useCallback(
    ({ requiresProfileCompletion, user, credential }) => {
      if (requiresProfileCompletion && user) {
        // User needs to complete profile - close SignIn and open SignUp with Google user data.
        // `credential` is present for a brand-new user (deferred create); the account is
        // created from it at the final signup step, so no empty account is left behind.
        onClose({
          openSignUp: true,
          googleUserData: {
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            email: user.email || "",
            isGoogleAuth: true,
            credential: credential || null,
          },
        });
        return;
      }
      // Full profile exists, close and user is logged in
      onClose();
    },
    [onClose]
  );

  const SignInSchema = useCallback(
    () =>
      Yup.object().shape({
        email: Yup.string().email("Invalid email address").required("Required"),
        password: Yup.string().min(8, "Too Short!").required("Required"),
      }),
    []
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog
      BackdropComponent={styled(Backdrop, {
        name: "MuiModal",
        slot: "Backdrop",
        overridesResolver: (props, styles) => styles.backdrop,
      })({ zIndex: -1, backgroundColor: BACKDROP_COLOR })}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="body"
      transitionDuration={{ appear: 0 }}
    >
      <Card sx={cardPadding}>
        <CardContent className={classes.cardContent}>
          <Box display="flex" flexDirection="row" width="100%">
            <Box display="flex" flexGrow={1} />
            <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Hello! 👋</Typography>
            <Box display="flex" flexGrow={1} justifyContent="flex-end" m="-30px -30px 30px 30px">
              <IconButton onClick={onClose} color="primary">
                <Close />
              </IconButton>
            </Box>
          </Box>
          <Formik initialValues={{ email: "", password: "" }} validationSchema={SignInSchema} onSubmit={handleSubmitFormik}>
            {({ handleSubmit, isSubmitting }) => (
              <form onSubmit={handleSubmit} className={classes.form}>
                <GoogleSignInButton onSuccess={handleGoogleSuccess} activityId={activityId} deferCreation />
                <Box display="flex" alignItems="center" my={2}>
                  <Box flexGrow={1} height="1px" bgcolor="divider" />
                  <Typography sx={{ px: 2, color: "text.secondary" }}>or</Typography>
                  <Box flexGrow={1} height="1px" bgcolor="divider" />
                </Box>
                <TextField required name="email" label="Email" autoComplete="username" />
                <TextField required name="password" label="Password" type="password" autoComplete="current-password" />
                <Link className={classes.link} to="/forgot_password">
                  Forgotten your password?
                </Link>
                <Box my={3}>
                  <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}>
                    Sign in
                  </Button>
                </Box>
                {errors.map((text) => (
                  <Alert sx={{ marginBottom: 2 }} key={text} severity="error">
                    {text}
                  </Alert>
                ))}
                {unverifiedUserId && (
                  <Box sx={{ marginBottom: 2 }}>
                    <Button fullWidth variant="outlined" disabled={resendState.loading} onClick={handleResendVerification}>
                      {resendState.loading ? "Sending…" : "Resend verification email"}
                    </Button>
                    {resendState.message && (
                      <Alert sx={{ marginTop: 1 }} severity="info">
                        {resendState.message}
                      </Alert>
                    )}
                  </Box>
                )}
                <Divider />
                <Box my={2}>
                  <Typography sx={{ fontWeight: 600 }} align="center">
                    New to Student Volunteer Army?
                  </Typography>
                </Box>

                <Button fullWidth variant="outlined" onClick={handleNavigateToSignUp}>
                  Create an account
                </Button>
                <FormikErrorFocus offset={0} align="top" focusDelay={200} ease="linear" duration={1000} />
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Dialog>
  );
}

SignIn.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activityId: PropTypes.number,
};
